import React, {useEffect, useState} from 'react';
import {COUNTRIES} from '../../utils/CountryData';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import {ControlledEZPRSelect} from '../../utils/ControlledEZPRSelect.jsx';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import {ezprOrange} from '../../../_helpers';
import PropTypes from 'prop-types';
import {Checkbox, IconButton, Typography} from '@mui/material';

/**
 * A popup that split the address in many fields and regroup them on save
 *
 * @param {!Object} props
 * @return {JSX.Element}
 */
export const DeliveryAddressPopup = (props) => {
    /**
     *
     * @type {DeliveryAddress}
     */
    const [deliveryAddress, setDeliveryAddress] = useState(props.value || {});
    const [alternateAddressDisabled, setAlternateAddressDisabled] = useState(!!(props.value && props.value.alternateAddress));

    useEffect(() => {
        const newValue = props.value || {};
        setDeliveryAddress(newValue);

        if (newValue.alternateAddress) {
            setAlternateAddressDisabled(true);
        } else {
            setAlternateAddressDisabled(false);
        }
    }, [props.value]);

    /**
     *
     * @param {string} property
     * @param {string} value
     */
    const updateProperty = (property, value) => {
        setDeliveryAddress((prevState) => {
            const temp = {
                ...prevState,
            };
            temp[property] = value;
            if (property !== 'alternateAddress') {
                if (!temp.alternateAddress) {
                    temp.alternateAddress = true;
                    setAlternateAddressDisabled(true);
                }
            }
            return temp;
        });
    };

    const countryNameFromCode = (code) => {
        return COUNTRIES.find((c) => c.iso2 === code || c.iso3 === code)?.name || '';
    };

    const countryCodeFromName = (name) => {
        return COUNTRIES.find((c) => c.name === name)?.iso2 || '';
    };

    return (<Dialog
        open={props.open}
        maxWidth={'sm'}
        fullWidth={true}
        onClose={props.handleClose}
    >
        <div className='pr-popup-no-header'>
            <IconButton
                aria-label="close"
                onClick={props.handleClose}
                sx={{
                    color: ezprOrange,
                }}>
                <CloseIcon
                    sx={{
                        width: '50px',
                        height: '50px',
                    }}
                />
            </IconButton>
        </div>
        <DialogContent>
            <Grid
                container
                spacing={2}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Grid item xs={4}>
                    <Box component={'p'} sx={{m: 0}}>Name:</Box>
                </Grid>
                <Grid item xs={8}>
                    <TextField
                        fullWidth={true}
                        variant={'standard'}
                        defaultValue={deliveryAddress.name}
                        onChange={(event) => updateProperty('name', event.target.value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Box component={'p'} sx={{m: 0}}>Name 2:</Box>
                </Grid>
                <Grid item xs={8}>
                    <TextField
                        fullWidth={true}
                        variant={'standard'}
                        defaultValue={deliveryAddress.name2}
                        onChange={(event) => updateProperty('name2', event.target.value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Box component={'p'} sx={{m: 0}}>House no/Street:</Box>
                </Grid>
                <Grid item xs={8} className={'pr-editor-search-supplier-popup-one-time-vendor-form-double-field'}>
                    <TextField
                        sx={{width: '20%'}}
                        variant={'standard'}
                        defaultValue={deliveryAddress.houseNumber}
                        onChange={(event) => updateProperty('houseNumber', event.target.value)}
                    />
                    <TextField
                        fullWidth={true}
                        variant={'standard'}
                        defaultValue={deliveryAddress.street}
                        onChange={(event) => updateProperty('street', event.target.value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Box component={'p'} sx={{m: 0}}>Postal code:</Box>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        fullWidth={true}
                        variant={'standard'}
                        defaultValue={deliveryAddress.postalCode}
                        onChange={(event) => updateProperty('postalCode', event.target.value)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Box component={'p'} sx={{m: 0}}>City:</Box>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        fullWidth={true}
                        variant={'standard'}
                        defaultValue={deliveryAddress.city}
                        onChange={(event) => updateProperty('city', event.target.value)}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Box component={'p'} sx={{m: 0}}>State/Province/Area:</Box>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        fullWidth={true}
                        variant={'standard'}
                        defaultValue={deliveryAddress.state}
                        onChange={(event) => updateProperty('state', event.target.value)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Box component={'p'} sx={{m: 0}}>Country:</Box>
                </Grid>
                <Grid item xs={4}>
                    <ControlledEZPRSelect
                        freeSolo={false}
                        options={COUNTRIES.map((c) => c.name)}
                        value={countryNameFromCode(deliveryAddress.country)}
                        handleChange={(_, value) => updateProperty('country', countryCodeFromName(value))}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Typography>Alternate delivery address: </Typography>
                </Grid>
                <Grid item xs={8}>
                    <Checkbox
                        checked={!!deliveryAddress?.alternateAddress}
                        onChange={(event) => updateProperty('alternateAddress', event.target.checked)}
                        disabled={alternateAddressDisabled}
                    />
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button
                onClick={() => {
                    props.onSave({});
                }}
            >
                Clear
            </Button>
            <Button
                sx={{
                    'backgroundColor': ezprOrange,
                    'color': 'white',
                    '&:hover': {
                        backgroundColor: '#FC9225',
                        color: 'white',
                    },
                }}
                onClick={() => {
                    const address = {
                        name: deliveryAddress.name,
                        name2: deliveryAddress.name2,
                        houseNumber: deliveryAddress.houseNumber,
                        street: deliveryAddress.street,
                        postalCode: deliveryAddress.postalCode,
                        city: deliveryAddress.city,
                        state: deliveryAddress.state,
                        country: deliveryAddress.country,
                        alternateAddress: deliveryAddress.alternateAddress || false,
                    };
                    props.onSave(address);
                }}
            >
                Apply
            </Button>
        </DialogActions>
    </Dialog>);
};

DeliveryAddressPopup.propTypes = {
    value: PropTypes.object,
    onSave: PropTypes.func,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
};
